import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { MarkdownMonacoEditor } from '@/components/context-sources'
import { SkillSectionActions } from '@/components/projects'
import { useSkillDetailContext } from '@/hooks'
import { SKILL_CONTENT_MAX, SKILL_DESCRIPTION_MAX, SKILL_NAME_MAX } from '@/lib/skillLimits'
import { cn } from '@/lib/utils'

const labelClass = 'text-xs font-bold leading-[160%] tracking-[0.015em] text-foreground'

export function SkillConfigPage() {
  const {
    skill,
    canEdit,
    displayName,
    onDisplayNameChange,
    internalName,
    onInternalNameChange,
    internalNameError,
    description,
    onDescriptionChange,
    content,
    onContentChange,
    contentTooLong,
    showValidation,
    isSaving,
  } = useSkillDetailContext()

  const isNew = skill === null
  const fieldsLocked = !canEdit || (isNew && isSaving)
  const displayNameMissing = showValidation && canEdit && !displayName.trim()
  let contentError: string | null = null
  if (showValidation && canEdit && !content.trim()) contentError = 'Content is required.'
  else if (showValidation && contentTooLong) contentError = `The content must not exceed ${SKILL_CONTENT_MAX} characters (currently ${content.length}).`

  return (
    <div className="flex h-full flex-col gap-6 overflow-y-auto rounded-2xl border border-border bg-card p-5" data-testid="skill-config-section">
      <SkillSectionActions />

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="skill-display-name" className={labelClass}>
            Display name <span aria-hidden="true">*</span>
          </Label>
          <Input
            id="skill-display-name"
            value={displayName}
            onChange={e => onDisplayNameChange(e.target.value)}
            readOnly={fieldsLocked}
            disabled={!canEdit}
            maxLength={SKILL_NAME_MAX}
            placeholder="Skill name"
            aria-required={!fieldsLocked}
            aria-invalid={displayNameMissing}
            aria-describedby={displayNameMissing ? 'skill-display-name-message' : undefined}
            className={cn('h-10 rounded-lg', displayNameMissing && 'border-destructive')}
            data-testid="skill-display-name-input"
          />
          {displayNameMissing && (
            <p id="skill-display-name-message" className="text-xs text-destructive" role="alert">Display name is required</p>
          )}
        </div>
        <div className="space-y-2">
          <Label htmlFor="skill-internal-name" className={labelClass}>
            Internal Name <span aria-hidden="true">*</span>
          </Label>
          <Input
            id="skill-internal-name"
            value={internalName}
            onChange={e => onInternalNameChange(e.target.value)}
            readOnly={!isNew || isSaving}
            disabled={!isNew}
            maxLength={SKILL_NAME_MAX}
            placeholder="skill-internal-name"
            aria-required={isNew}
            aria-invalid={internalNameError !== null}
            aria-describedby={internalNameError ? 'skill-internal-name-message' : undefined}
            className={cn('h-10 rounded-lg', internalNameError && 'border-destructive')}
            data-testid="skill-internal-name-input"
          />
          {internalNameError && (
            <p id="skill-internal-name-message" className="text-xs text-destructive" role="alert">
              {internalNameError}
            </p>
          )}
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="skill-version" className={cn(labelClass, 'text-muted-foreground')}>
          Version <span aria-hidden="true">*</span>
        </Label>
        <Input
          id="skill-version"
          value={skill?.latestVersion?.version ?? ''}
          readOnly
          disabled
          placeholder="1.0.0"
          className="h-10 rounded-lg"
          data-testid="skill-version-input"
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="skill-description" className={labelClass}>Short description</Label>
        <Input
          id="skill-description"
          value={description}
          onChange={e => onDescriptionChange(e.target.value)}
          readOnly={fieldsLocked}
          disabled={!canEdit}
          maxLength={SKILL_DESCRIPTION_MAX}
          placeholder="Add a short description"
          className="h-10 rounded-lg"
          data-testid="skill-description-input"
        />
      </div>

      <div
        className="flex min-h-[320px] flex-1 flex-col gap-2"
        role="group"
        aria-labelledby="skill-content-label"
        aria-describedby={contentError ? 'skill-content-message' : undefined}
      >
        <p id="skill-content-label" className={labelClass}>Skill.md <span aria-hidden="true">*</span></p>
        <div className="relative min-h-0 flex-1 overflow-hidden rounded-2xl border border-border">
          <MarkdownMonacoEditor
            value={content}
            onChange={onContentChange}
            readOnly={fieldsLocked}
            dataTestId="skill-content-editor"
            plain
            ariaLabel={canEdit ? `SKILL.md content, required${contentError ? `, ${contentError}` : ''}` : 'SKILL.md content, read-only'}
          />
          {!content && canEdit && (
            <p className="pointer-events-none absolute left-5 top-4 text-sm italic text-muted-foreground" aria-hidden="true">
              Add a description here that defines the skill you&apos;re creating.
            </p>
          )}
        </div>
        {contentError && (
          <p id="skill-content-message" className="text-xs text-destructive" role="alert">
            {contentError}
          </p>
        )}
        <div className="flex justify-end text-xs text-muted-foreground">
          <span className={contentTooLong ? 'font-bold text-destructive' : ''} data-testid="skill-content-char-count">
            {content.length}/{SKILL_CONTENT_MAX}
          </span>
        </div>
      </div>
    </div>
  )
}
